// Cellery Soapbox — in-iframe highlight rendering (CONTRACTS §6).
// Same-origin: the parent mutates iframe.contentDocument directly. Highlights
// are presentational only and are NEVER persisted back to storage. Wrapping is
// done per-text-node so we never destroy or re-parent existing element nodes.

import { HIGHLIGHT_BORDER } from "../types";
import type { Anchor, Annotation } from "../types";

const STYLE_ID = "soapbox-hl-style";
const HL_CLASS = "soapbox-hl";
const RESOLVED_CLASS = "soapbox-hl--resolved";

/** Inject the highlight stylesheet into the iframe <head> once. */
export function ensureHighlightStyles(doc: Document): void {
  if (doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement("style");
  style.id = STYLE_ID;
  // Every app injection carries data-soapbox so serializeCleanHtml can strip the
  // whole class of them generically, rather than by remembering each id (§6a).
  style.setAttribute("data-soapbox", "hl-style");
  // Subtle, non-intrusive: a pale butter border, no fill, so authored content
  // shows through unchanged. Resolved is distinguished by border STYLE (dotted)
  // and a fainter color — a non-color signal, so it's not color-alone (a11y).
  style.textContent = `
    .${HL_CLASS} {
      background: transparent;
      color: inherit;
      border: 1px solid ${HIGHLIGHT_BORDER};
      border-radius: 2px;
      cursor: pointer;
      box-decoration-break: clone;
      -webkit-box-decoration-break: clone;
    }
    .${HL_CLASS}.${RESOLVED_CLASS} {
      border-style: dotted;
      border-color: rgba(232, 217, 168, 0.6);
    }
    /* Touch: a 1px border is hard to tap, so add a translucent butter fill and
       a little padding to enlarge the hit area (no layout shift). */
    @media (hover: none), (pointer: coarse) {
      .${HL_CLASS} {
        background: rgba(246, 223, 164, 0.4);
        padding: 0 1px;
        margin: 0 -1px;
        touch-action: manipulation;
      }
    }
  `;
  const head = doc.head ?? doc.documentElement;
  head.appendChild(style);
}

/** Open any <details> ancestors of a node so wrapped content is visible. */
function openDetailsAround(node: Node | null): void {
  let el: Element | null =
    node && node.nodeType === Node.ELEMENT_NODE
      ? (node as Element)
      : node?.parentElement ?? null;
  while (el) {
    if (el.tagName === "DETAILS") {
      (el as HTMLDetailsElement).open = true;
    }
    el = el.parentElement;
  }
}

/** Collect the text nodes a Range touches, in document order. */
function intersectingTextNodes(doc: Document, range: Range): Text[] {
  const common = range.commonAncestorContainer;
  const scope: Node =
    common.nodeType === Node.TEXT_NODE ? common.parentNode ?? common : common;
  const out: Text[] = [];
  const walker = doc.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
  let n = walker.nextNode();
  while (n) {
    const t = n as Text;
    if (t.length > 0 && range.intersectsNode(t)) {
      out.push(t);
    }
    n = walker.nextNode();
  }
  return out;
}

/**
 * Wrap a Range in <mark class="soapbox-hl" data-ann-id> nodes. Each text node
 * is wrapped with its own sub-range so surroundContents always succeeds.
 */
export function highlightRange(
  doc: Document,
  range: Range,
  annId: string,
  resolved: boolean
): void {
  if (range.collapsed) return;

  openDetailsAround(range.startContainer);
  openDetailsAround(range.endContainer);

  const { startContainer, startOffset, endContainer, endOffset } = range;
  const nodes = intersectingTextNodes(doc, range);

  for (const node of nodes) {
    const from = node === startContainer ? startOffset : 0;
    const to = node === endContainer ? endOffset : node.length;
    if (from >= to) continue;

    const sub = doc.createRange();
    sub.setStart(node, from);
    sub.setEnd(node, to);

    const mark = doc.createElement("mark");
    mark.className = resolved ? `${HL_CLASS} ${RESOLVED_CLASS}` : HL_CLASS;
    mark.setAttribute("data-ann-id", annId);
    try {
      sub.surroundContents(mark);
    } catch {
      // Boundary the sub-range couldn't cleanly wrap; skip this fragment.
    }
  }
}

/** Unwrap a single <mark>, restoring its children and merging text runs. */
function unwrapMark(mark: Element): void {
  const parent = mark.parentNode;
  if (!parent) return;
  while (mark.firstChild) {
    parent.insertBefore(mark.firstChild, mark);
  }
  parent.removeChild(mark);
  (parent as Element | Document).normalize?.();
}

/** Remove every soapbox highlight from the document. */
export function clearHighlights(doc: Document): void {
  const marks = Array.from(doc.querySelectorAll(`mark.${HL_CLASS}`));
  for (const mark of marks) {
    unwrapMark(mark);
  }
}

/** Remove the injected highlight stylesheet (clearHighlights does NOT). */
export function removeHighlightStyles(doc: Document): void {
  doc.getElementById(STYLE_ID)?.remove();
}

/** Asset tags whose presence is decided by the AUTHOR, never by the viewer. */
const ASSET_TAGS = "script, style, link, meta, base";

/**
 * Multiset of asset-tag outerHTML, captured from a freshly loaded document.
 *
 * Taken in the iframe's onLoad BEFORE the app injects anything, so it is the
 * author's own head/body asset inventory. serializeCleanHtml uses it to drop
 * anything that appeared afterwards — our own injections, and, importantly,
 * nodes a BROWSER EXTENSION injected into the same-origin frame (a real v2
 * contaminant: <style id="jc-autofill-preloader-styles">).
 *
 * Sound only because the edit frame runs script-free (§6c): with the bundle's
 * own JS disabled, nothing rewrites its asset tags between load and serialize,
 * so "absent from the baseline" reliably means "not authored".
 */
export function captureAssetTags(doc: Document): Map<string, number> {
  const seen = new Map<string, number>();
  for (const el of Array.from(doc.querySelectorAll(ASSET_TAGS))) {
    const key = el.outerHTML;
    seen.set(key, (seen.get(key) ?? 0) + 1);
  }
  return seen;
}

export interface SerializeOptions {
  /**
   * Load-time asset inventory from captureAssetTags. When supplied, asset tags
   * absent from it are dropped. Omit on the export path (pdf.ts), which
   * snapshots a POST-script DOM where script-injected styles are load-bearing.
   */
  authoredAssets?: Map<string, number>;
}

/**
 * Serialize the iframe document to clean HTML for persistence WITHOUT mutating
 * the live (still-editable) DOM: clone the root, then strip ALL app-injected
 * decoration (highlight marks + the injected stylesheet + edit-mode placeholder
 * styling) and editing affordances (contenteditable attrs) from the clone so
 * none of it is written back to storage. Preserves the doctype, which
 * documentElement.outerHTML drops.
 */
export function serializeCleanHtml(
  doc: Document,
  opts: SerializeOptions = {}
): string {
  const root = doc.documentElement.cloneNode(true) as HTMLElement;
  // Generic strip of everything the app injected, plus the legacy edit-fonts
  // link by id — it predates the data-soapbox convention and leaked into
  // already-published bundles.
  for (const el of Array.from(root.querySelectorAll("[data-soapbox]"))) {
    el.remove();
  }
  root.querySelector(`#${STYLE_ID}`)?.remove();
  root.querySelector("#soapbox-edit-fonts")?.remove();
  for (const mark of Array.from(root.querySelectorAll(`mark.${HL_CLASS}`))) {
    const parent = mark.parentNode;
    if (!parent) continue;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
  }
  root.normalize();
  for (const el of Array.from(root.querySelectorAll("[contenteditable]"))) {
    el.removeAttribute("contenteditable");
  }
  // Edit-mode placeholder marking is an attribute (not a node), so authored
  // elements survive untouched once it is removed.
  for (const el of Array.from(root.querySelectorAll("[data-soapbox-empty]"))) {
    el.removeAttribute("data-soapbox-empty");
  }
  if (opts.authoredAssets) {
    const budget = new Map(opts.authoredAssets);
    for (const el of Array.from(root.querySelectorAll(ASSET_TAGS))) {
      const left = budget.get(el.outerHTML) ?? 0;
      if (left > 0) budget.set(el.outerHTML, left - 1);
      else el.remove();
    }
  }
  const prefix = doc.doctype ? "<!DOCTYPE html>\n" : "";
  return prefix + root.outerHTML;
}

/** Clear, then redraw all annotation highlights using the supplied resolver. */
export function redrawHighlights(
  doc: Document,
  annotations: Annotation[],
  resolve: (anchor: Anchor) => Range | null
): void {
  clearHighlights(doc);
  for (const ann of annotations) {
    const range = resolve(ann.anchor);
    if (range) {
      highlightRange(doc, range, ann.annId, ann.resolved);
    }
  }
}

/**
 * Listen for clicks on highlights inside the iframe document and report the
 * clicked annotation id to the parent. Returns a cleanup function.
 */
export function attachHighlightClick(
  doc: Document,
  onClick: (annId: string) => void
): () => void {
  const handler = (e: Event) => {
    const target = e.target as Element | null;
    const mark = target?.closest?.(`mark.${HL_CLASS}[data-ann-id]`) as
      | HTMLElement
      | null;
    if (mark) {
      const id = mark.getAttribute("data-ann-id");
      if (id) onClick(id);
    }
  };
  doc.addEventListener("click", handler, true);
  return () => doc.removeEventListener("click", handler, true);
}
